import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';

const { FiMenu, FiBell, FiUser, FiCalendar, FiChevronDown } = FiIcons;

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const { dateFilter, setDateFilter } = useData();

  // Get current page title based on path
  const getPageTitle = () => {
    const path = location.pathname.split('/')[1];
    switch (path) {
      case 'dashboard': return 'Dashboard';
      case 'properties': return 'Properties';
      case 'guest-pages': return 'Guest Pages';
      case 'automation': return 'Automation';
      case 'email-settings': return 'Email Settings';
      default: return 'Dashboard';
    }
  };

  return (
    <div className="min-h-screen bg-[#eef2f6]"> {/* Reference bg color */}
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      {/* Main Content */}
      <div className="lg:ml-64 min-h-screen flex flex-col transition-all duration-300">
        
        {/* Top Header */}
        <header className="h-20 bg-[#eef2f6] px-6 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 -ml-2 mr-4 text-gray-600 hover:bg-white rounded-lg transition-colors"
            >
              <SafeIcon icon={FiMenu} className="text-2xl" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800 hidden sm:block">{getPageTitle()}</h1>
          </div>

          <div className="flex items-center space-x-4">
            
            {/* Calendar Filter Option */}
            <div className="hidden md:flex items-center bg-white rounded-full px-4 py-2.5 shadow-sm border border-gray-100 relative group hover:border-blue-200 transition-colors cursor-pointer">
              <SafeIcon icon={FiCalendar} className="text-gray-500 mr-2 group-hover:text-blue-500 transition-colors" />
              <select 
                value={dateFilter} 
                onChange={(e) => setDateFilter(e.target.value)}
                className="bg-transparent border-none text-sm text-gray-700 font-medium focus:ring-0 cursor-pointer outline-none appearance-none pr-6 w-32"
              >
                <option value="Today">Today</option>
                <option value="This Week">This Week</option>
                <option value="This Month">This Month</option>
                <option value="Next Month">Next Month</option>
                <option value="All Time">All Time</option>
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <SafeIcon icon={FiChevronDown} className="text-gray-400 text-xs" />
              </div>
            </div>

            {/* Notification Icon */}
            <button className="p-2.5 bg-white rounded-full text-gray-500 hover:text-blue-600 shadow-sm border border-gray-100 transition-colors relative">
              <SafeIcon icon={FiBell} className="text-lg" />
              <span className="absolute top-2 right-2.5 w-2 h-2 bg-red-500 rounded-full border border-white"></span>
            </button>

            {/* Profile */}
            <div className="flex items-center space-x-3 pl-2">
              <div className="text-right hidden md:block">
                <p className="text-sm font-semibold text-gray-800">{user?.name || 'User'}</p>
                <p className="text-xs text-gray-500">Admin</p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center border-2 border-white shadow-sm overflow-hidden">
                <SafeIcon icon={FiUser} className="text-blue-600" />
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 px-6 pb-8 pt-2">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;